import { apiGet, apiPost, apiPut, apiDelete } from './client';
import type { PagedResponse, SearchParams } from './types';

// --- Notification Document ---

export interface Notification {
  id: string;
  tenant_id: string;
  user_id: string;
  title: string;
  body: string;
  channel: string; // email / in_app / sms / push
  status: string; // pending / sending / sent / failed / read
  event_type: string;
  related_entity_type?: string;
  related_entity_id?: string;
  read_at?: string;
  sent_at?: string;
  error_message?: string;
  retry_count: number;
  metadata?: Record<string, string>;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}

export interface CreateNotificationRequest {
  user_id: string;
  title: string;
  body: string;
  channel: string;
  event_type: string;
  related_entity_type?: string;
  related_entity_id?: string;
  metadata?: Record<string, string>;
}

export interface NotificationFilters extends SearchParams {
  channel?: string;
  status?: string;
  event_type?: string;
  user_id?: string;
}

// --- Notification Channel Config ---

export interface NotificationChannelConfig {
  id: string;
  tenant_id: string;
  channel_type: string; // email / sms
  provider: string; // smtp / sendgrid / sms_stub
  is_active: boolean;
  config_data: string; // masked JSON in responses
  priority: number;
  max_retries: number;
  retry_delay_seconds: number;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}

export interface CreateChannelConfigRequest {
  channel_type: string;
  provider: string;
  is_active?: boolean;
  config_data: string;
  priority?: number;
  max_retries?: number;
  retry_delay_seconds?: number;
}

export interface UpdateChannelConfigRequest {
  is_active?: boolean;
  config_data?: string;
  priority?: number;
  max_retries?: number;
  retry_delay_seconds?: number;
}

export interface ChannelConfigFilters extends SearchParams {
  channel_type?: string;
}

// --- Notification API ---

export const getNotifications = (params: NotificationFilters = {}) =>
  apiGet<PagedResponse<Notification>>('/api/v1/notifications', params);

export const getNotification = (id: string) =>
  apiGet<Notification>(`/api/v1/notifications/${id}`);

export const sendNotification = (data: CreateNotificationRequest) =>
  apiPost<Notification>('/api/v1/notifications/send', data);

export const markNotificationRead = (id: string) =>
  apiPost<void>(`/api/v1/notifications/${id}/mark-read`, {});

export const markAllNotificationsRead = () =>
  apiPost<{ marked_count: number }>('/api/v1/notifications/mark-all-read', {});

// --- Channel Config API ---

export const getChannelConfigs = (params: ChannelConfigFilters = {}) =>
  apiGet<PagedResponse<NotificationChannelConfig>>('/api/v1/notification-channel-configs', params);

export const getChannelConfig = (id: string) =>
  apiGet<NotificationChannelConfig>(`/api/v1/notification-channel-configs/${id}`);

export const createChannelConfig = (data: CreateChannelConfigRequest) =>
  apiPost<NotificationChannelConfig>('/api/v1/notification-channel-configs', data);

export const updateChannelConfig = (id: string, data: UpdateChannelConfigRequest) =>
  apiPut<NotificationChannelConfig>(`/api/v1/notification-channel-configs/${id}`, data);

export const deleteChannelConfig = (id: string) =>
  apiDelete(`/api/v1/notification-channel-configs/${id}`);

export const testSendChannelConfig = (id: string) =>
  apiPost<{ success: boolean; external_message_id?: string }>(`/api/v1/notification-channel-configs/${id}/test-send`, {});
